import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import axiosInstance from '../utils/axiosConfig';
import AlertModal from './AlertModal';
import './Inbox.css';
import './GroupMailbox.css';
import { enhanceHtmlLinks, linkifyPlainText } from '../utils/mailLinks';

const GroupMailbox = ({ currentUser }) => {
  const { groupId } = useParams();
  const navigate = useNavigate();
  const [group, setGroup] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [replyTargetMail, setReplyTargetMail] = useState(null);
  const [replyMode, setReplyMode] = useState('reply');
  const [showReplyBox, setShowReplyBox] = useState(false);
  const [replyBody, setReplyBody] = useState('');
  const [showQuotedContent, setShowQuotedContent] = useState(false);
  const [isSendingReply, setIsSendingReply] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');

  const [showMembersModal, setShowMembersModal] = useState(false);
  const [memberInput, setMemberInput] = useState('');
  const [pendingMembers, setPendingMembers] = useState([]);
  const [memberSuggestions, setMemberSuggestions] = useState([]);
  const [showMemberSuggestions, setShowMemberSuggestions] = useState(false);
  const [memberError, setMemberError] = useState('');
  const [savingMembers, setSavingMembers] = useState(false);

  const normalizeEmail = (value) => String(value || '').trim().toLowerCase();

  const isGroupCreator = String(group?.createdBy || '') === String(currentUser?._id || '');

  const looksLikeHtmlBody = (value) => typeof value === 'string' && /<[a-z][\s\S]*>/i.test(value);

  const parseSenderDisplay = (from) => {
    const raw = String(from || '').trim();
    if (!raw) return 'Unknown';
    const match = raw.match(/^(?:"([^"]+)"|([^<]+?))\s*<([^>]+)>$/);
    if (match) {
      const name = (match[1] || match[2] || '').trim();
      if (name) return name;
      return match[3].split('@')[0];
    }
    if (raw.includes('@')) {
      const name = raw.split('@')[0];
      return name.charAt(0).toUpperCase() + name.slice(1).replace(/[._-]/g, ' ');
    }
    return raw;
  };

  const extractEmail = (from) => {
    const raw = String(from || '').trim();
    const match = raw.match(/<([^>]+)>/);
    if (match) return match[1].trim().toLowerCase();
    if (raw.includes('@')) return raw.toLowerCase();
    return raw.toLowerCase();
  };

  const isOwnMessage = (mail) => normalizeEmail(extractEmail(mail?.from)) === normalizeEmail(currentUser?.email);

  const getSenderLabel = (mail) => {
    const name = parseSenderDisplay(mail.from);
    return isOwnMessage(mail) ? `${name} (You)` : name;
  };

  const getSenderInitial = (email) => {
    const name = parseSenderDisplay(email);
    return (name.charAt(0) || '?').toUpperCase();
  };

  const getPlainBody = (mail) => {
    const body = String(mail?.body || '');
    return body.replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim();
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return '';
    const now = new Date();
    const isToday =
      date.getDate() === now.getDate() &&
      date.getMonth() === now.getMonth() &&
      date.getFullYear() === now.getFullYear();

    if (isToday) {
      return date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      });
    }
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  const formatFullDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'numeric',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    });
  };

  const renderMailBody = (mail) => {
    const body = String(mail?.body || '').trim();
    if (looksLikeHtmlBody(body)) {
      return (
        <div
          className="body-content mail-html-body"
          dangerouslySetInnerHTML={{ __html: enhanceHtmlLinks(body) }}
        />
      );
    }
    return (
      <div
        className="body-content"
        style={{ whiteSpace: 'pre-wrap' }}
        dangerouslySetInnerHTML={{ __html: linkifyPlainText(body) }}
      />
    );
  };

  const getQuotedContent = (mail) => {
    if (!mail) return '';
    return `On ${formatFullDate(mail.createdAt)}, ${mail.from} wrote:\n${getPlainBody(mail)}`;
  };

  const getReplyToLabel = (mode, mail) => {
    const groupName = group?.name || 'group';
    if (mode === 'replyAll') {
      const memberEmails = (group?.members || [])
        .map((member) => member.email)
        .filter(Boolean)
        .join(', ');
      return memberEmails ? `${groupName} (${memberEmails})` : `${groupName} (all members)`;
    }
    if (mail) {
      return `${parseSenderDisplay(mail.from)} (${extractEmail(mail.from)})`;
    }
    return groupName;
  };

  const threadMessages = useMemo(
    () => [...messages].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt)),
    [messages]
  );

  const latestMessage = threadMessages.length > 0 ? threadMessages[threadMessages.length - 1] : null;

  const loadGroupMailbox = useCallback(async (showLoading = true) => {
    if (showLoading) setLoading(true);
    setError('');

    try {
      const [groupsResponse, messagesResponse] = await Promise.all([
        axiosInstance.get('/api/groups'),
        axiosInstance.get(`/api/groups/${groupId}/messages`),
      ]);

      const matchedGroup = (groupsResponse.data.data || []).find((item) => item._id === groupId);
      setGroup(matchedGroup || null);
      setMessages(messagesResponse.data.data || []);
    } catch (err) {
      console.error('Error loading group mailbox:', err);
      setError(err.response?.data?.message || 'Could not load group mailbox');
    } finally {
      if (showLoading) setLoading(false);
    }
  }, [groupId]);

  useEffect(() => {
    if (groupId) {
      loadGroupMailbox();
    }
  }, [groupId, loadGroupMailbox]);

  useEffect(() => {
    const query = memberInput.trim();
    if (!showMembersModal || query.length < 1) {
      setMemberSuggestions([]);
      return undefined;
    }

    const timeoutId = setTimeout(async () => {
      try {
        const response = await axiosInstance.get('/api/users/suggestions', {
          params: { q: query },
        });
        if (response.data.success) {
          const existingEmails = new Set((group?.members || []).map((m) => normalizeEmail(m.email)));
          setMemberSuggestions(
            (response.data.data || []).filter((user) => (
              normalizeEmail(user.email) !== normalizeEmail(currentUser?.email) &&
              !pendingMembers.includes(normalizeEmail(user.email)) &&
              !existingEmails.has(normalizeEmail(user.email))
            ))
          );
        }
      } catch (err) {
        setMemberSuggestions([]);
      }
    }, 180);

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memberInput, showMembersModal, currentUser?.email, pendingMembers, group]);

  const openMembersModal = () => {
    setMemberInput('');
    setPendingMembers([]);
    setMemberSuggestions([]);
    setShowMemberSuggestions(false);
    setMemberError('');
    setShowMembersModal(true);
  };

  const closeMembersModal = () => {
    setShowMembersModal(false);
    setMemberInput('');
    setPendingMembers([]);
    setMemberSuggestions([]);
    setShowMemberSuggestions(false);
    setMemberError('');
  };

  const addPendingMember = (rawEmail) => {
    const email = normalizeEmail(rawEmail).replace(/,$/, '');
    if (!email) return;
    if (!/^[a-zA-Z0-9._-]+@atozasmail\.in$/.test(email)) {
      setMemberError('Only @atozasmail.in users can be added.');
      return;
    }
    const alreadyMember = (group?.members || []).some((m) => normalizeEmail(m.email) === email);
    if (alreadyMember) {
      setMemberError('That user is already a member.');
      setMemberInput('');
      return;
    }
    if (pendingMembers.includes(email) || email === normalizeEmail(currentUser?.email)) {
      setMemberInput('');
      return;
    }
    setPendingMembers((prev) => [...prev, email]);
    setMemberInput('');
    setMemberSuggestions([]);
    setShowMemberSuggestions(false);
    setMemberError('');
  };

  const removePendingMember = (email) => {
    setPendingMembers((prev) => prev.filter((item) => item !== email));
  };

  const handleMemberKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === ',') {
      event.preventDefault();
      addPendingMember(memberInput);
    }
    if (event.key === 'Backspace' && !memberInput && pendingMembers.length > 0) {
      setPendingMembers((prev) => prev.slice(0, -1));
    }
  };

  const handleAddMembers = async () => {
    let members = [...pendingMembers];
    const pending = normalizeEmail(memberInput).replace(/,$/, '');
    if (pending) {
      if (!/^[a-zA-Z0-9._-]+@atozasmail\.in$/.test(pending)) {
        setMemberError('Only @atozasmail.in users can be added.');
        return;
      }
      if (pending !== normalizeEmail(currentUser?.email) && !members.includes(pending)) {
        members = [...members, pending];
      }
    }

    if (members.length === 0) {
      setMemberError('Add at least one member.');
      return;
    }

    setMemberError('');
    setSavingMembers(true);
    try {
      const response = await axiosInstance.patch(`/api/groups/${groupId}/members`, { members });
      if (response.data.success) {
        setGroup(response.data.data);
        window.dispatchEvent(new CustomEvent('groupsUpdated'));
        setPendingMembers([]);
        setMemberInput('');
        setAlertMessage(response.data.message || 'Members added');
        setShowAlert(true);
        setShowMembersModal(false);
      } else {
        setMemberError(response.data.message || 'Could not add members');
      }
    } catch (err) {
      setMemberError(err.response?.data?.message || 'Could not add members');
    } finally {
      setSavingMembers(false);
    }
  };

  const handleRemoveMember = async (member) => {
    if (!member?._id) return;
    if (!window.confirm(`Remove ${member.username || member.email} from this group?`)) return;

    try {
      const response = await axiosInstance.delete(`/api/groups/${groupId}/members/${member._id}`);
      if (response.data.success) {
        setGroup(response.data.data);
        window.dispatchEvent(new CustomEvent('groupsUpdated'));
      } else {
        setMemberError(response.data.message || 'Could not remove member');
      }
    } catch (err) {
      setMemberError(err.response?.data?.message || 'Could not remove member');
    }
  };

  const openCompose = () => {
    navigate(`/compose?groupId=${encodeURIComponent(groupId)}`);
  };

  const startReply = (mail, mode = 'reply') => {
    setReplyTargetMail(mail);
    setReplyMode(mode);
    setShowReplyBox(true);
    setReplyBody('');
    setShowQuotedContent(false);
  };

  const handleReply = (mail) => {
    if (isOwnMessage(mail)) {
      setAlertMessage('You cannot reply to your own message. Use Reply all or Mail group.');
      setShowAlert(true);
      return;
    }
    startReply(mail, 'reply');
  };

  const handleReplyAll = (mail) => {
    startReply(mail, 'replyAll');
  };

  const handleForward = (mail) => {
    if (!mail?._id) {
      setAlertMessage('Unable to forward this message');
      setShowAlert(true);
      return;
    }
    navigate(`/compose?forward=${encodeURIComponent(mail._id)}`);
  };

  const handleCancelReply = () => {
    setShowReplyBox(false);
    setReplyBody('');
    setShowQuotedContent(false);
    setReplyTargetMail(null);
  };

  const handleSendReply = async () => {
    if (!replyBody.trim()) {
      setAlertMessage('Please enter a reply message');
      setShowAlert(true);
      return;
    }
    if (!replyTargetMail) {
      setAlertMessage('Select a message to reply to');
      setShowAlert(true);
      return;
    }

    setIsSendingReply(true);
    try {
      const subjectBase = replyTargetMail.subject || '';
      const replySubject = subjectBase.startsWith('Re:') ? subjectBase : `Re: ${subjectBase}`;
      const fullBody = `${replyBody.trim()}\n\n${getQuotedContent(replyTargetMail)}`;

      let response;
      if (replyMode === 'replyAll') {
        response = await axiosInstance.post('/api/mail/send', {
          groupId,
          subject: replySubject,
          body: fullBody,
        });
      } else {
        const recipientEmail = extractEmail(replyTargetMail.from);
        if (!recipientEmail || !recipientEmail.includes('@')) {
          setAlertMessage('Could not find recipient email');
          setShowAlert(true);
          return;
        }
        response = await axiosInstance.post('/api/mail/send', {
          to: recipientEmail,
          subject: replySubject,
          body: fullBody,
        });
      }

      if (response.data.success) {
        handleCancelReply();
        if (replyMode === 'replyAll') {
          await loadGroupMailbox(false);
        }
        const recipientName = parseSenderDisplay(replyTargetMail.from);
        setAlertMessage(
          replyMode === 'replyAll'
            ? 'Reply sent to group'
            : `Reply sent to ${recipientName}`
        );
        setShowAlert(true);
      } else {
        setAlertMessage(response.data.message || 'Failed to send reply');
        setShowAlert(true);
      }
    } catch (err) {
      console.error('Error sending group reply:', err);
      setAlertMessage(err.response?.data?.message || 'Could not send reply');
      setShowAlert(true);
    } finally {
      setIsSendingReply(false);
    }
  };

  if (loading) {
    return (
      <div className="inbox-container">
        <div className="loading">Loading group...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="inbox-container">
        <div className="error-message">{error}</div>
        <button type="button" onClick={() => loadGroupMailbox()} className="retry-button">
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="inbox-container premium-mail-dashboard group-mailbox group-thread-view">
      <div className="inbox-header">
        <div>
          <p className="inbox-eyebrow">Shared label group</p>
          <h2>{group?.name || 'Group'}</h2>
        </div>
        <div className="inbox-header-actions">
          <button
            type="button"
            className="feature-chip group-members-chip"
            onClick={openMembersModal}
            title="View / manage members"
          >
            {group?.members?.length || 0} members
          </button>
          <button type="button" className="group-compose-btn" onClick={openCompose}>
            Mail group
          </button>
        </div>
      </div>

      <Link to={`/compose?groupId=${encodeURIComponent(groupId)}`} className="mobile-compose-btn" title="Mail group">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
          <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
        </svg>
        <span>Mail group</span>
      </Link>

      <div className="inbox-content group-thread-content">
        <div className="group-thread-panel">
          <div className="mail-toolbar">
            <div className="toolbar-left">
              <button type="button" className="toolbar-btn" onClick={() => loadGroupMailbox(false)} title="Refresh">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M23 4v6h-6"/>
                  <path d="M1 20v-6h6"/>
                  <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
                </svg>
              </button>
            </div>
          </div>

          {threadMessages.length === 0 ? (
            <div className="empty-inbox">
              <p>No group mail yet</p>
              <p className="empty-hint">Send the first message to everyone in this group.</p>
            </div>
          ) : (
            <div className="mail-detail-content group-thread-detail">
              <div className="reply-messages-container group-thread-messages">
                {threadMessages.map((mail) => (
                  <div
                    key={mail._id}
                    className={`reply-message-item ${replyTargetMail?._id === mail._id && showReplyBox ? 'active-reply-target' : ''}`}
                  >
                    <div className="reply-message-header">
                      <div className="reply-sender-info">
                        <div
                          className="reply-sender-avatar"
                          style={{ backgroundColor: isOwnMessage(mail) ? '#667eea' : '#1a73e8', color: 'white' }}
                        >
                          {getSenderInitial(mail.from)}
                        </div>
                        <div className="reply-sender-details">
                          <span className={`reply-sender-name ${isOwnMessage(mail) ? 'is-you' : ''}`}>
                            {getSenderLabel(mail)}
                          </span>
                          <span className="reply-date">{formatDate(mail.createdAt)}</span>
                        </div>
                      </div>
                      <div className="group-message-actions">
                        <button type="button" className="group-msg-action-btn" onClick={() => handleReply(mail)} title="Reply">
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M10 9V5l-7 7 7 7v-4.1c5 0 8.5 1.6 11 5.1-1-5-4-10-11-11z"/>
                          </svg>
                        </button>
                        <button type="button" className="group-msg-action-btn" onClick={() => handleReplyAll(mail)} title="Reply all">
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M7 7v4H2l7 7 7-7h-5V7H7zm10 0h-4v2h4v6h-3l5 5 5-5h-3V7h-4z"/>
                          </svg>
                        </button>
                        <button type="button" className="group-msg-action-btn" onClick={() => handleForward(mail)} title="Forward">
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M12 8V4l8 8-8 8v-4H4V8z"/>
                          </svg>
                        </button>
                      </div>
                    </div>
                    {mail.subject ? (
                      <div className="group-thread-subject">{mail.subject}</div>
                    ) : null}
                    <div className="reply-message-body">
                      {renderMailBody(mail)}
                    </div>
                  </div>
                ))}
              </div>

              <div className="mail-action-buttons">
                <button
                  type="button"
                  className="reply-btn"
                  onClick={() => latestMessage && handleReply(latestMessage)}
                  disabled={!latestMessage}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M10 9V5l-7 7 7 7v-4.1c5 0 8.5 1.6 11 5.1-1-5-4-10-11-11z"/>
                  </svg>
                  <span>Reply</span>
                </button>
                <button
                  type="button"
                  className="reply-btn reply-all-btn"
                  onClick={() => latestMessage && handleReplyAll(latestMessage)}
                  disabled={!latestMessage}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M7 7v4H2l7 7 7-7h-5V7H7zm10 0h-4v2h4v6h-3l5 5 5-5h-3V7h-4z"/>
                  </svg>
                  <span>Reply all</span>
                </button>
                <button
                  type="button"
                  className="forward-btn"
                  onClick={() => latestMessage && handleForward(latestMessage)}
                  disabled={!latestMessage}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 8V4l8 8-8 8v-4H4V8z"/>
                  </svg>
                  <span>Forward</span>
                </button>
              </div>

              {showReplyBox && replyTargetMail && (
                <div className="reply-box">
                  <div className="reply-header">
                    <span className="reply-to-label">
                      To: {getReplyToLabel(replyMode, replyTargetMail)}
                    </span>
                    <button type="button" className="reply-close-btn" onClick={handleCancelReply}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                      </svg>
                    </button>
                  </div>
                  <div className="reply-content-wrapper">
                    <textarea
                      className="reply-textarea"
                      value={replyBody}
                      onChange={(e) => setReplyBody(e.target.value)}
                      placeholder="Type your reply here..."
                      rows="6"
                      disabled={isSendingReply}
                    />
                    {showQuotedContent && (
                      <div className="quoted-content">
                        {getQuotedContent(replyTargetMail)}
                      </div>
                    )}
                    <button
                      type="button"
                      className="reply-ellipsis"
                      onClick={() => setShowQuotedContent(!showQuotedContent)}
                      aria-label="Toggle quoted content"
                    >
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                        <circle cx="12" cy="12" r="10" fill="#e8eaed"/>
                        <circle cx="8.5" cy="12" r="1.5" fill="#5f6368"/>
                        <circle cx="12" cy="12" r="1.5" fill="#5f6368"/>
                        <circle cx="15.5" cy="12" r="1.5" fill="#5f6368"/>
                      </svg>
                    </button>
                  </div>
                  <div className="reply-actions">
                    <button
                      type="button"
                      className="send-reply-btn"
                      onClick={handleSendReply}
                      disabled={isSendingReply || !replyBody.trim()}
                    >
                      {isSendingReply ? 'Sending...' : 'Send'}
                    </button>
                    <button
                      type="button"
                      className="cancel-reply-btn"
                      onClick={handleCancelReply}
                      disabled={isSendingReply}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {showMembersModal && (
        <div className="gm-members-overlay" role="presentation" onClick={closeMembersModal}>
          <div
            className="gm-members-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="gm-members-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="gm-members-head">
              <h2 id="gm-members-title">{group?.name || 'Group'} members</h2>
              <button type="button" className="gm-members-close" onClick={closeMembersModal} aria-label="Close">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                </svg>
              </button>
            </div>

            <div className="gm-members-list">
              {(group?.members || []).map((member) => {
                const isCreatorMember = String(member._id) === String(group?.createdBy);
                return (
                  <div className="gm-member-row" key={member._id || member.email}>
                    <span className="gm-member-avatar">
                      {(member.username || member.email || 'U').charAt(0).toUpperCase()}
                    </span>
                    <span className="gm-member-copy">
                      <strong>{member.username || member.email}</strong>
                      <small>{member.email}{isCreatorMember ? ' • Owner' : ''}</small>
                    </span>
                    {isGroupCreator && !isCreatorMember && (
                      <button
                        type="button"
                        className="gm-member-remove"
                        onClick={() => handleRemoveMember(member)}
                        title="Remove member"
                        aria-label={`Remove ${member.email}`}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
                        </svg>
                      </button>
                    )}
                  </div>
                );
              })}
            </div>

            {isGroupCreator ? (
              <div className="gm-members-add">
                <span className="gm-members-add-label">Add members</span>
                <div className="gm-member-input-wrap">
                  {pendingMembers.map((email) => (
                    <span className="gm-member-chip" key={email}>
                      {email}
                      <button type="button" onClick={() => removePendingMember(email)} aria-label={`Remove ${email}`}>x</button>
                    </span>
                  ))}
                  <input
                    type="email"
                    value={memberInput}
                    onChange={(e) => {
                      setMemberInput(e.target.value);
                      setShowMemberSuggestions(true);
                    }}
                    onKeyDown={handleMemberKeyDown}
                    onFocus={() => setShowMemberSuggestions(true)}
                    onBlur={() => {
                      setTimeout(() => setShowMemberSuggestions(false), 120);
                    }}
                    placeholder="user@atozasmail.in"
                    autoComplete="off"
                  />
                </div>
                {showMemberSuggestions && memberSuggestions.length > 0 && (
                  <div className="gm-member-suggestions">
                    {memberSuggestions.map((user) => (
                      <button
                        type="button"
                        className="gm-member-suggestion"
                        key={user._id || user.email}
                        onMouseDown={(event) => {
                          event.preventDefault();
                          addPendingMember(user.email);
                        }}
                      >
                        <span className="gm-member-avatar">
                          {(user.username || user.email || 'U').charAt(0).toUpperCase()}
                        </span>
                        <span className="gm-member-copy">
                          <strong>{user.username || user.email}</strong>
                          <small>{user.email}</small>
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <p className="gm-members-note">Only the group owner can add or remove members.</p>
            )}

            {memberError && <div className="gm-members-error">{memberError}</div>}

            <div className="gm-members-actions">
              <button type="button" className="gm-members-cancel" onClick={closeMembersModal}>
                Close
              </button>
              {isGroupCreator && (
                <button
                  type="button"
                  className="gm-members-save"
                  onClick={handleAddMembers}
                  disabled={savingMembers || (pendingMembers.length === 0 && !memberInput.trim())}
                >
                  {savingMembers ? 'Adding...' : 'Add members'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      <AlertModal
        isOpen={showAlert}
        onClose={() => setShowAlert(false)}
        message={alertMessage}
      />
    </div>
  );
};

export default GroupMailbox;
